import classNames from "classnames";
import { CANONICAL_PATH_EXCEPTION, ENVIRONMENT_DOMAIN } from "./constants";
import { SHOWS } from "../../constants/shows";
import { REGIONS_LIST } from "../../constants/regions";
import { SECTIONS, TOPIC_SECTION_LIST } from "../../constants/sections";
import { PAGE_TYPES, CUSTOM_TRACKING } from "../../constants/tracking";
import { isBrowser } from "../environmentDetection/library";
import {
    SUBJECTS_SET,
    CONTENTLIST_TAGS,
    CONTENTLIST_CATEGORY_SLUG_OVERRIDE,
} from "../../constants/content";
import LINEUP_SLUGS from "../../constants/slugs/lineup";
import CATEGORY_SLUGS from "../../constants/slugs/category";
import logger from "../logger/library";

/**
 * Given a path return the "base section" segment.
 *
 * The "base section" is the part of the path after the initial forward
 * slash, used to indicate top-level content area/category, e.g. news,
 * sports, arts, etc.
 *
 * @param {string} path a URL path
 * @returns {string} the the "section" path segment
 */
export function getBaseSection(path) {
    let section = "";
    let sectionEnd = 0;
    let sectionBegin = 0;

    if (path) {
        // Normalize the section name; note that indexOf() is case-sensitive.
        path = path.toLowerCase();
        // Gets index of first forward slash after the initial one.
        sectionBegin = path.charAt(0) === "/" ? 1 : 0;
        sectionEnd = path.indexOf("/", sectionBegin);

        if (sectionEnd < 0) {
            // There were no forward slashes after the first; the whole
            // path segment after the initial character is the section.
            section = path.substring(sectionBegin);
        } else {
            // Grab the segment between the first forward slash and the
            // second.
            section = path.substring(sectionBegin, sectionEnd);
        }
    } else {
        logger.error("(getBaseSection) no path provided");
    }

    return section;
}

/**
 * Create a url based on the given information.
 *
 * @param {boolean} external (REQUIRED) indicates if this url is external or internal.  A 'true' value
 *                           requires that the url parameter not be an empty string.
 * @param {string} url (OPTIONAL) a URL - can be empty if the url to be built is internal to the application.
 * @param {Object} parts (OPTIONAL) contains the urlCategory, slug and sourceId information to build an internal
 *                              application url.
 *
 * @returns {string} the appropriate url for an internal or external link.
 */
export function makeURL(external, url, parts) {
    let path = url;

    if (external !== true) {
        const { urlCategory, slug, sourceId, queryParams } = parts;
        const categorySegment = urlCategory.toLowerCase();
        let formattedQueryParams = "";

        if (queryParams) {
            formattedQueryParams = Object.keys(queryParams).reduce(
                (previous, current, index) => {
                    return (previous += `${index === 0 ? "?" : "&"}${current}=${
                        queryParams[current]
                    }`);
                },
                ""
            );
        }

        path = `/${categorySegment}/${slugify(
            slug
        )}-${sourceId}${formattedQueryParams}`;
    }

    return path;
}

/**
 * Slugifies given string using the given separator and removing any special characters.
 *
 * @param {string} s (REQUIRED) string to be slugified.
 * @param {string} separator {REQUIRED) separator to be used in place of spaces.
 *
 * @returns {string} slugified string value.
 */
export function slugify(s, separator) {
    if (!s) {
        return "";
    }

    if (!separator) {
        separator = "-";
    }
    let slug = s
        .toString()
        // NB: not required to escape the '.' inside a RegExp character range.
        .replace(/[\s.]+/g, separator)
        .toLowerCase()
        .replace(new RegExp(`[^a-z0-9${separator}]`, "g"), separator)
        .replace(new RegExp(`${separator}+`, "g"), separator);
    if (slug.charAt(slug.length - 1) === separator) {
        slug = slug.substring(0, slug.length - 1);
    }
    while (slug.charAt(0) === "-") {
        slug = slug.substring(1, slug.length);
    }
    return slug;
}

/**
 * Slugifies and sets prefix for normalization
 * helps create valid CSS classes
 *
 * @param {string} prefix  prefix string
 * @param {string} className {REQUIRED} string to be slugified
 * @param {string} suffix The suffix string
 * @param {bool} shouldSlugify The slugify flag
 *
 * @returns {string} slugified string value.
 */
export function normalizeClass(
    prefix = "",
    className,
    suffix = "",
    shouldSlugify = true
) {
    if (!className || className.toLowerCase() === "none") {
        return "";
    }

    // Check if we need to slugify the classname before appending prefix/suffix
    const updatedClass = shouldSlugify ? `-${slugify(className)}` : className;

    if (!prefix && updatedClass.match(/^[0-9]+/)) {
        prefix = "app";
    }

    return `${prefix}${updatedClass}${suffix}`;
}

/**
 * Removes the cbc.ca domain from a url (if it exists), making it relative.
 *
 * @param {string} url (REQUIRED) URL to remove the domain from.
 *
 * @returns {string} a url without the cbc.ca domain.
 */
export function removeUrlDomain(url) {
    return typeof url === "string"
        ? url.replace(
              /^((https?:\/\/)?(www\.)?((ocelot-.+\.int|qa|dev).nm\.)?cbc\.ca)(\/beta)?/,
              ``
          )
        : url;
}

/** given a valid url return the path
 * @param {string} url - valid url
 * @param {boolean} keepCase - if true, preserve the character case
 * @return {string} path from the url without the leading slash
 */
export const getPath = (url, keepCase = false) => {
    url = keepCase ? url.trim() : url.trim().toLowerCase();
    if (url) {
        let i = url.indexOf("/");
        if (i === -1) {
            return url;
        }
        i++;
        //has scheme
        if (url.charAt(i) === "/") {
            i++;
            i = url.indexOf("/", i);
            if (i === -1 || i === url.length - 1) {
                return "";
            }
            i++;
        }
        if (hasQuery(url)) {
            return url.substring(i, url.lastIndexOf("?"));
        }
        if (hasFragment(url)) {
            return url.substring(i, url.lastIndexOf("#"));
        }
        return url.substring(i);
    }
    return url;
};

export const hasQuery = (url) => {
    if (url) {
        return url.lastIndexOf("?") > -1;
    }
    return false;
};

export const hasFragment = (url) => {
    if (url) {
        return url.lastIndexOf("#") > -1;
    }
    return false;
};

/**
 * Get the section segment at the given index.
 * @param {string} path The url path.
 * @param {number} index Index for the desired section segment.
 * @return {string} The full section segment at the given index.
 */
export const getSectionByIndex = (path, index) => {
    const sections = path.split("/");
    if (sections.length === 0 || sections.length <= index) {
        return "";
    }
    return sections[index] || "";
};

/**
 * Get full section path - remove trailing slash if there is one.
 * @param {string} path The url path.
 * @param {boolean} hasContentId TRUE if path contains a content ID, FALSE otherwise.
 * @return {string} The full section path for the given url.
 */
export const getSection = (path, hasContentId) => {
    if (hasContentId && path) {
        return path.substring(0, path.lastIndexOf("/")); // section is everything up to the content title/id segment
    }

    if (path.endsWith("/")) {
        return path.substring(0, path.length - 1); // We don't want a trailing / in the section.
    }

    return path;
};

/**
 *
 * @param {String} url The canonical/share URL for the page
 * @returns {String} The input URL with any /beta segments removed
 */
export const stripBetaPrefix = (url) => {
    // Replace beginning and trailing forward slashes to
    // hopefully avoid any weirdness around accidentally
    // capturing part of a story URL starting with /beta.

    if (url) {
        return url.replace(/\/beta\//, "/");
    }
};

/**
 * This is mainly useful for Canonical and OG:url where absolute link is needed
 * Polopoly Json sometimes will output relative URL path eg. /sports/videos/
 *
 * @param {String} url The canonical/share URL for the page
 * @returns {String} Will append cbc domain to make absolute when necessary
 */
export const makeAbsoluteCBCDomain = (url) => {
    if (isAbsoluteUrl(url)) {
        return url;
    }

    const regexPattern = new RegExp(`/^(http(s)?:)?/${ENVIRONMENT_DOMAIN}/i`);
    if (!regexPattern.test(url)) {
        // Strip slash prefix on relative path for easy concatenation
        url = url.replace(/^\//, "");
        return `https://${ENVIRONMENT_DOMAIN}/${url}`;
    }
    return url;
};

/* There's an exception for Homepage canonical path, needs to drop the "/homepage"
 * @param {string} path Refer to appState.path
 * @return {string} the path after filtered by exceptions
 */
export const pathException = (path) => {
    if (path in CANONICAL_PATH_EXCEPTION) {
        return CANONICAL_PATH_EXCEPTION[path];
    }
    return path;
};

/*
 * https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
 */
export const isAbsoluteUrl = (url) => {
    if (typeof url !== "string") {
        throw new TypeError("Expected a string");
    }

    let r = new RegExp("^(?:[a-z]+:)?//", "i");

    return r.test(url);
};

/**
 * Private - getShowParams
 * In case the landing page is a Show, different API parameters need to be sent
 * @param {array} showList - The list of Shows for the given section
 * @param {string} baseSection - The URL baseSection, e.g. "arts"
 * @param {string} subSection - The URL subSection, e.g. "eddyrogo"
 * @param {string} path - The URL path, includes everything up to the :title
 * @returns {object} The object containing Show API parameters: { contentSection }
 */
export const getShowParams = (showList, baseSection, subSection, path) => {
    let showParams = null;
    let showName = subSection;
    let sectionShowList = showList[baseSection];

    // The Show API url needs a custom "showSlug" parameter
    // Check if the route was sent via the alias or the showSlug parameter
    if (sectionShowList && showName) {
        let currentShow = null;
        sectionShowList.forEach((show) => {
            // In certain situations, the showName is nested in the url path, lower than the subSection
            const isNestedShow =
                show.nestedPath && show.nestedUrl === `/${path}`;

            // Check if the current show is the one we're looking for
            if (
                showName === show.alias ||
                showName === show.showSlug ||
                isNestedShow
            ) {
                currentShow = show;
            }
        });

        if (currentShow) {
            showParams = {
                contentSection: path,
                metaTitle: currentShow.title,
                config: currentShow,
            };
        }
    }
    return showParams;
};

/**
 * Certain landing pages have banners, determine if this is one of them
 * @param {Object} sectionsList - The list of pages with banners
 * @param {string} path - The url path of the page
 * @param {string} baseSection - The baseSection of the path
 * @param {string} subSection - The subSection of the path
 * @param {boolean} hasMenu - Optional menu for the banner
 * @returns {object} the pageIntro params
 */
export const getPageIntroParams = (
    sectionsList,
    path,
    baseSection,
    subSection,
    hasMenu
) => {
    const showParams = getShowParams(SHOWS, baseSection, subSection, path);
    let currentIntro = sectionsList[path] ? sectionsList[path] : null;
    if (showParams !== null) {
        try {
            Object.assign(currentIntro, showParams.config);
        } catch (error) {
            return null;
        }
    }

    if (currentIntro) {
        Object.assign(currentIntro, { hasMenu, baseSection, subSection });
    }

    return currentIntro;
};

/**
 * Capitalize the first letter of the string
 * @param {string} text
 * @returns {string}
 */
export const capitalizeFirstLetter = (text) => {
    if (!text) {
        return "";
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Formats the section name for displaying to the user
 * @param {string} sectionName
 * @returns {string}
 */
export const formatSectionName = (sectionName) => {
    if (!sectionName) {
        return "";
    }

    const sectionConfig = SECTIONS[sectionName];
    let formattedName = sectionConfig && sectionConfig.title;
    if (formattedName) {
        return formattedName;
    }

    return capitalizeFirstLetter(sectionName);
};

/**
 * Format the meta tag title for the page, mainly for Landing pages
 * @param {Object} app - The app state object
 * @returns {String} The formatted page title for meta tags
 */
export const formatMetaTitle = (app) => {
    const metaBase = formatSectionName(app.baseSection);
    let metaSub = app.subSection
        ? `- ${formatSectionName(app.subSection)}`
        : "";

    const showParams = getShowParams(
        SHOWS,
        app.baseSection,
        app.subSection?.trim()
    );
    if (showParams) {
        if (showParams.metaTitle) {
            metaSub = `- ${showParams.metaTitle}`;
        }
    }

    let finalString = "";

    if (app.pageType === "topic") {
        finalString = `CBC ${decodeURIComponent(app.path)
            .split("/")
            .join(" ")}`;
    } else {
        finalString = `CBC ${metaBase} ${metaSub}`;
    }

    return finalString;
};

/**
 * Get the category slug of the given section, defined in app/constants/slugs/category.js
 * @param {String} section - The section for which we need the category slug
 * @returns {String} The category slug
 */
export const getCategorySlug = (section) => {
    return CATEGORY_SLUGS[section] || "";
};

/**
 * Get the lineup id (pubQ) of the given section, defined in app/constants/slugs/lineup.js
 * @param {String} section - The section for which we need the lineup id
 * @returns {String} The lineup id
 */
export const getLineupSlug = (section) => {
    return LINEUP_SLUGS[section] || "";
};

/**
 * Get the subject id of the given section, defined in app/constants/content.js
 * @param {String} section - The section for which we need the subject id
 * @returns {String} The subject id
 */
export const getSubjectsSet = (section) => {
    return SUBJECTS_SET[section] || "";
};

/**
 * Get the tags for the given section, as defined app/constants/content.js
 * @param {string} tags - an expression for finding content
 * @returns {string} if defined the value, or an empty string
 */
export const getTags = (section) => {
    return CONTENTLIST_TAGS[section] || "";
};

/**
 * Get the category slug override for the given section,
 * @param {string} section - an expression for finding content
 * @returns {string} if defined the value, or null if not found
 */
export const getCategorySlugOverride = (section) => {
    if (section in CONTENTLIST_CATEGORY_SLUG_OVERRIDE) {
        const value = CONTENTLIST_CATEGORY_SLUG_OVERRIDE[section];
        if (value === "empty") return "";
        return getCategorySlug(value);
    }
    return null;
};

/**
 * Check if the current page is a Local News one and return the object from config
 * E.g. /news/canada/ottawa
 * @param {string} path The path of the current page
 * @returns {object} The regional object as defined in the config
 */
export const getRegionalPath = (path) => {
    const region = REGIONS_LIST.find((region) => {
        // If we're on the main regional page
        if (region && region.path === path) {
            return true;
        }
        // Otherwise, we might be on the sub-regional pages so go over the region's navigation pages
        // e.g. community and check if there's an indication to display Regional Intro section
        if (region && region.navigation) {
            const regionSubPage = region.navigation.some((navLink) => {
                return (
                    navLink.link &&
                    navLink.link === `/${path}` &&
                    navLink.isCommunity &&
                    navLink.isCommunity === true
                );
            });
            return regionSubPage;
        }
    });

    return region;
};

/**
 * Normalizes the the region url (i.e. news/canada/toronto/community to news/canada/toronto)
 * @param {string} path the current app path
 * @param {string} baseUrl the baseUrl to fall back to
 * @returns {string} Normalized path
 */
export const normalizeRegionUrl = (path, baseUrl) => {
    const isValidPath = REGIONS_LIST.map((obj) => {
        return obj.path;
    }).indexOf(path);
    if (isValidPath > -1) {
        return `/${path}`;
    }

    const splitPath = path && typeof path === "string" && path.split("/");
    if (splitPath.length > 2) {
        return `/${splitPath[0]}/${splitPath[1]}/${splitPath[2]}`;
    }
    return baseUrl;
};

/**
 * Removes storyPage from path
 * i.e. if your path is news/canada/toronto/wayne-millard-trial-day-8-1.4701780
 *      this should return news/canada/toronto
 * @param {string} path
 * @returns {string} path without story
 */
export const stripStoryFromNav = (path) => {
    const splitPath = path.split("/");
    splitPath.splice("-1", "1");
    return splitPath.join("/");
};

/**************************************************
            UTILS FOR PAGE TYPES
**************************************************/

/**
 * Check if the current page is Landing page, based on category slugs defined in default.yaml
 * @param {object} app The app state
 * @returns {object} An indicator whether it's a landing page
 */
export const isLandingPage = (app = {}) => {
    return app && getCategorySlug(app.section);
};

/**
 * Check if the current page is a story, i.e. detail
 * @param {object} app The app state
 * @returns {object} An indicator whether it's a story/detail page
 */
export const isDetailPage = (app = {}) => {
    return app && app.contentId;
};

/**
 * Check if the current page is a show, based on the objects defined in /constants/shows.js
 * @param {object} app The app state
 * @returns {object} The show object or NULL
 */
export const isShowPage = (app = {}) => {
    if (app) {
        return getShowParams(
            SHOWS,
            app.baseSection,
            app.subSection ? app.subSection.trim() : "",
            app.section
        );
    }
    return null;
};

/**
 * Check if the current page is a custom one, hardcoded in Ocelot
 * @param {object} app The app state
 * @returns {object} The custom object or NULL
 */
export const isCustomPage = (app = {}) => {
    if (!app || !app.section) {
        return null;
    }

    // Add other sections where we don't have tracking info from
    // https://api-gw-dev.radio-canada.ca/aggregate-content/v1/categories
    const customTracking = CUSTOM_TRACKING.find(
        (trackingItem) => trackingItem.id === app.section
    );
    return customTracking;
};

/**
 * Check if the current page is a topic page
 * @param {object} app The app state
 * @returns {object} An indicator whether it's a topic page or not
 */
export const isTopicPage = (app = {}) => {
    let isTopic = false;
    if (app) {
        isTopic = TOPIC_SECTION_LIST.some((item) => {
            return app.path.includes(`${item}/`);
        });
    }

    return isTopic;
};

const isPlayerLandingPage = (app = {}) => {
    if (!app.section) {
        return false;
    }
    return (
        app.section.startsWith("player") &&
        !app.section.startsWith("player/play")
    );
};

/**
 * Determines what kind of a page it is, e.g. Landing, Show, Detail etc
 * @param {object} app The app state
 * @returns {string} A string determining a page type
 */
export const getPageType = (app = {}) => {
    if (!app) {
        return PAGE_TYPES.NOTFOUND;
    }

    if (isDetailPage(app)) {
        return PAGE_TYPES.DETAIL;
    }

    if (isTopicPage(app)) {
        return PAGE_TYPES.TOPIC;
    }

    if (isLandingPage(app)) {
        return PAGE_TYPES.LANDING;
    }

    if (isShowPage(app)) {
        return PAGE_TYPES.SHOW;
    }

    if (isCustomPage(app)) {
        return PAGE_TYPES.CUSTOM;
    }

    if (isPlayerLandingPage(app)) {
        return PAGE_TYPES.INDEX;
    }

    return PAGE_TYPES.BLANK;
};

export const isDetail = (appObj = {}) => {
    return Boolean(appObj.contentId);
};

export const isOlympics = (app = {}) => {
    return (
        /^olympics-(summer|winter|paralympics)$/.test(app.baseNavSection) ||
        /sports\/olympics\/(summer|winter|paralympics)/.test(app.path)
    );
};

export const getOlympicsSeason = (app = {}) => {
    let season = null;
    if ("olympics-summer" === app.baseNavSection) {
        season = "summer";
    } else if ("olympics-winter" === app.baseNavSection) {
        season = "winter";
    } else if ("olympics-paralympics" === app.baseNavSection) {
        season = "paralympics";
    } else if (app.path.includes("sports/paralympics")) {
        season = "paralympics";
    } else {
        const match =
            /sports\/olympics\/(?<season>summer|winter)/.exec(app.path) ?? {};
        season = match.groups?.season || null;
    }
    return season;
};

export const getPageClassNames = (appData) => {
    // Determine the theme and template classnames to be added to AppContainer
    const themeClassNames = classNames(
        { detail: isDetail(appData) },
        normalizeClass("", appData.baseSection, "Theme", false),
        normalizeClass("", appData.pageType, "Page", false),
        normalizeClass("", appData.contentType, "Type", false),
        normalizeClass("", appData.subSection, "Section", false)
    );
    return themeClassNames;
};

/**
 * Predicate function to determine whether sectional data should be fetched
 *
 * @param {string} category the category slug for the section
 * @param {string} lineup the lineup slug for the section
 * @param {boolean} sectionExists whether the section already exists in state
 */
export function shouldFetchSectionalContent({
    category,
    lineup,
    sectionExists,
}) {
    if (lineup === null && category === null) {
        return false;
    }

    if (category === undefined || lineup === undefined || sectionExists) {
        return false;
    }
    return true;
}

export const getDomainHost = () => {
    return isBrowser() ? window.__DOMAIN_HOST__ : process.env.DOMAIN_HOST;
};
